import {
  Client,
  BufferTrackerReact as BufferTracker,
} from '@buffer-mono/tracking-plan'

import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'

/** The GraphQL operation every event from this hook describes. */
const OPERATION = 'commentsCountsByPost'

/**
 * Tells the two signals apart in the `context` field, so an omitted row can be
 * counted separately from a request that never delivered counts.
 */
const OmittedRows = 'omittedRows'
const RequestFailed = 'requestFailed'

/**
 * Post IDs already reported, keyed `<reason>:<postId>`.
 *
 * The cap is one event per unique post ID per reason, for the life of the page.
 * Module scope, not a ref, because the by-post sidepanel remounts on every
 * filter or channel change, and a per-mount cap would let one persistent
 * problem report again after each remount. A reload starts a fresh cap, which
 * keeps reload count as the one remaining measure of how often a problem
 * recurs.
 *
 * A post ID key beats a time window here. The sidepanel refetches counts every
 * few seconds, so a window on a busy organization would spend its budget
 * re-reporting the same posts while a newly affected post waits for the window
 * to open. Keying on the post ID makes volume scale with the size of the post
 * list, which is bounded, instead of with refetch frequency, which is not.
 */
const reportedPostIdKeys = new Set<string>()

/** Clears the cap so a test starts from a known state. */
export function resetCommentsCountsTrackingCap(): void {
  reportedPostIdKeys.clear()
}

/**
 * Marks every post ID as reported for `reason` and says whether any of them was
 * new. One batch reports at most one event, so a chunk of 40 posts that all
 * miss a row does not become 40 events.
 */
function claimUnreportedPostIds(
  reason: string,
  postIds: readonly string[],
): boolean {
  let hasUnreported = false

  for (const postId of postIds) {
    const key = `${reason}:${postId}`
    if (!reportedPostIdKeys.has(key)) {
      hasUnreported = true
      reportedPostIdKeys.add(key)
    }
  }

  return hasUnreported
}

type TrackOmittedCountsRows = {
  requestedPostIds: readonly string[]
  missingPostIds: readonly string[]
}

type TrackCountsRequestFailed = {
  requestedPostIds: readonly string[]
  errorMessage: string
  statusCode: number | null
  /** Where the failure came from: a transport problem or a GraphQL error. */
  source: 'frontend' | 'backend'
}

export type UseCommentsCountsTracking = {
  trackOmittedCountsRows: (payload: TrackOmittedCountsRows) => void
  trackCountsRequestFailed: (payload: TrackCountsRequestFailed) => void
}

/**
 * Reports the two ways per-post comment counts go missing without anyone
 * noticing: a successful response that carries no row for a requested post, and
 * a request that failed outright.
 *
 * Both fire `Engagement API Error` with `operation: commentsCountsByPost`.
 * Payloads carry post and organization IDs and counts only — never comment
 * text, author names or handles.
 */
export function useCommentsCountsTracking(): UseCommentsCountsTracking {
  const commonTrackingProperties = useCommonTrackingProperties()

  const trackOmittedCountsRows = ({
    requestedPostIds,
    missingPostIds,
  }: TrackOmittedCountsRows): void => {
    if (missingPostIds.length === 0) return
    if (!claimUnreportedPostIds(OmittedRows, missingPostIds)) return

    try {
      BufferTracker.engagementAPIError({
        clientName: Client.PublishWeb,
        source: 'backend',
        operation: OPERATION,
        errorMessage: 'Comment counts response omitted requested post IDs',
        statusCode: null,
        context: JSON.stringify({
          reason: OmittedRows,
          requestedCount: requestedPostIds.length,
          missingCount: missingPostIds.length,
          missingPostIds,
        }),
        ...commonTrackingProperties,
      })
    } catch {
      // Telemetry must not break the counts fetch it observes.
    }
  }

  const trackCountsRequestFailed = ({
    requestedPostIds,
    errorMessage,
    statusCode,
    source,
  }: TrackCountsRequestFailed): void => {
    if (!claimUnreportedPostIds(RequestFailed, requestedPostIds)) return

    try {
      BufferTracker.engagementAPIError({
        clientName: Client.PublishWeb,
        source,
        operation: OPERATION,
        errorMessage,
        statusCode,
        context: JSON.stringify({
          reason: RequestFailed,
          requestedCount: requestedPostIds.length,
        }),
        ...commonTrackingProperties,
      })
    } catch {
      // Telemetry must not break the counts fetch it observes.
    }
  }

  return { trackOmittedCountsRows, trackCountsRequestFailed }
}
